/**
 * Participant entry and menu from the instance's module chain (#685).
 *
 * The chain (API_ENDPOINTS.INSTANCE_PARTICIPANT_CHAIN) lists the participant
 * modules in chain order, each with its route, menu entries and whether it is
 * unlocked. Everything a participant can open — the top-left menu, where a PIN
 * or scenario pick lands, which version of a module a route shows — is read
 * from it. Replaces workshopMappingRoute.js and the v1/v2 flags.
 */
import API_ENDPOINTS from '../config/api';

/** Fetch an instance's participant chain; throws with the server's message. */
export async function fetchParticipantChain(instanceId) {
  const res = await fetch(API_ENDPOINTS.INSTANCE_PARTICIPANT_CHAIN(instanceId));
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `participant-chain ${res.status}`);
  return data;
}

/**
 * Where a participant starts: the first unlocked entry module, else the first
 * unlocked module, else the first entry module (the workshop has not reached
 * any yet, and its page says so), else the first module.
 * @returns {string|null}
 */
export function getEntryPath(chain) {
  // The instance may name the landing page itself (settings.participantEntry → chain.entry)
  if (chain?.entry?.route) return chain.entry.route;
  const items = (chain?.items || []).filter((i) => i.moduleId !== 'report.v1');
  const pick = items.find((i) => i.entry && i.unlocked)
    || items.find((i) => i.unlocked)
    || items.find((i) => i.entry)
    || items[0];
  return pick?.route || null;
}

/**
 * Entry URL with ?kid and the given extra params. The systemic map opens in
 * Kaikki (type=3) unless the caller says otherwise.
 * @param {object} chain participant chain
 * @param {string} instanceId
 * @param {Record<string, string>} [extraParams]
 */
export function buildEntryUrl(chain, instanceId, extraParams = {}) {
  const path = getEntryPath(chain);
  if (!path) throw new Error(`Instance ${instanceId} has no participant modules`);
  const params = new URLSearchParams();
  params.set('kid', instanceId);
  Object.entries(extraParams).forEach(([k, v]) => {
    if (v != null && v !== '' && k !== 'kid') params.set(k, String(v));
  });
  if (path === '/systemicmap' && !params.has('type')) params.set('type', '3');
  return `${path}?${params.toString()}`;
}

/** Fetch the chain and build the entry URL in one go (PIN, instance pick, lock device). */
export async function resolveEntryUrl(instanceId, extraParams = {}) {
  return buildEntryUrl(await fetchParticipantChain(instanceId), instanceId, extraParams);
}

/**
 * The top-left menu: one entry per module menu item, in chain order.
 * @param {object} chain participant chain
 * @param {string} language fi / en / sv
 * @returns {{ key: string, route: string, routes: string[], label: string,
 *   moduleId: string, family: string, wallView: string|null, unlocked: boolean,
 *   live: boolean, isReport: boolean }[]}
 */
// Tilannekuva is not a chain module: it is appended as the menu's last item
// whenever a chain exists (specs/workshop-dashboard.md, #1156). Per-language
// labels follow the manifests' idiom because this util has no t().
const DASHBOARD_MENU_LABELS = { fi: 'Tilannekuva', en: 'Dashboard', sv: 'Lägesbild' };

export function buildMenuItems(chain, language) {
  const out = [];
  for (const item of chain?.items || []) {
    const menu = item.menu?.length ? item.menu : [{ route: item.route, label: item.family, labels: {} }];
    menu.forEach((m, i) => {
      out.push({
        key: `${item.stepId}:${i}`,
        route: m.route,
        // The version URL shows the same module, so it marks the same menu entry
        routes: [m.route, ...(i === 0 ? [item.route, item.directRoute] : [])].filter(Boolean),
        label: m.labels?.[language] || m.label,
        moduleId: item.moduleId,
        family: item.family,
        wallView: i === 0 ? item.wallView || null : null,
        unlocked: Boolean(item.unlocked),
        live: Boolean(item.live),
        isReport: item.moduleId === 'report.v1',
      });
    });
  }
  if (out.length) {
    out.push({
      key: 'dashboard',
      route: '/tilannekuva',
      routes: ['/tilannekuva'],
      label: DASHBOARD_MENU_LABELS[language] || DASHBOARD_MENU_LABELS.fi,
      moduleId: null,
      family: 'dashboard',
      wallView: 'dashboard',
      // An ongoing-situation view, never gated on flow position; a workshop may
      // lock it for participants outright (chain.dashboard.locked, settings.dashboardLocked)
      unlocked: chain?.dashboard?.locked !== true,
      live: false,
      isReport: false,
    });
  }
  return out;
}

/** The menu entry for a path (with or without a trailing slug), or null. */
export function findMenuItemForPath(menuItems, pathname) {
  return menuItems.find((m) => m.routes.some((r) => pathname === r || pathname.startsWith(`${r}/`))) || null;
}

/** The chain's module of a family, e.g. 'impact_path' → 'impact_path.v2'; null when the chain has none. */
export function moduleIdForFamily(chain, family) {
  return chain?.items?.find((i) => i.family === family)?.moduleId || null;
}
